import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAllFreeServiceClaims,
  deleteFreeServiceClaim,
  updateFreeServiceClaim,
} from "../../../api/api";
import styles from "./FreeServiceManagement.module.css";

const FreeServiceManagement = () => {
  const navigate = useNavigate();
  const userRole = localStorage.getItem("adminRole");
  const isSuperadmin = userRole === "superadmin";
  // Stringers can search and view credits but not change them
  const isReadOnly = userRole === "stringer";

  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [editingClaim, setEditingClaim] = useState(null);
  const [editForm, setEditForm] = useState({
    totalFreeServices: 0,
    usedServices: 0,
  });

  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [claimToDelete, setClaimToDelete] = useState(null);

  useEffect(() => {
    fetchClaims();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showError = (msg) => {
    setError(msg);
    setTimeout(() => setError(""), 3000);
  };

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const fetchClaims = async () => {
    try {
      setLoading(true);
      const data = await getAllFreeServiceClaims();
      setClaims(Array.isArray(data) ? data : []);
      setError("");
    } catch (err) {
      console.error("Error fetching claims:", err);
      showError("Failed to load free service claims");
    } finally {
      setLoading(false);
    }
  };

  // --- Delete modal handlers ---
  const handleDeleteClick = (claimId) => {
    setClaimToDelete(claimId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteFreeServiceClaim(claimToDelete);
      setShowDeleteModal(false);
      setClaimToDelete(null);
      showSuccess("Free service claim deleted successfully");
      fetchClaims();
    } catch (err) {
      console.error("Error deleting claim:", err);
      setShowDeleteModal(false);
      setClaimToDelete(null);
      showError("Failed to delete claim");
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setClaimToDelete(null);
  };

  // --- Edit handlers ---
  const handleEditClick = (claim) => {
    setEditingClaim(claim.claim_id);
    setEditForm({
      totalFreeServices: Number(claim.total_free_services || 0),
      usedServices: Number(claim.used_services || 0),
    });
  };

  const handleCancelEdit = () => {
    setEditingClaim(null);
    setEditForm({ totalFreeServices: 0, usedServices: 0 });
    setError("");
  };

  const handleSaveEdit = async (claim) => {
    const claimId = claim.claim_id;
    const total = parseInt(editForm.totalFreeServices, 10);
    const used = parseInt(editForm.usedServices, 10);

    if (Number.isNaN(total) || Number.isNaN(used)) {
      showError("Total Services and Used Services must be valid numbers.");
      return;
    }
    if (total < used) {
      showError(`Total Services (${total}) cannot be less than Used Services (${used}).`);
      return;
    }

    try {
      const payload = {
        totalFreeServices: total,
        usedServices: used,
      };
      await updateFreeServiceClaim(claimId, payload);

      setEditingClaim(null);
      showSuccess("Free service claim updated successfully");
      fetchClaims();
    } catch (err) {
      console.error("Error updating claim:", err);
      showError(err.response?.data?.message || "Failed to update claim");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const [year, month, day] = dateString.slice(0, 10).split("-");
    return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const filteredClaims = claims.filter((claim) => {
    const term = searchTerm.toLowerCase();
    return (
      (claim.customer_name?.toLowerCase() || "").includes(term) ||
      (claim.customer_email?.toLowerCase() || "").includes(term) ||
      (claim.customer_phone?.toLowerCase() || "").includes(term) ||
      (claim.racket_name?.toLowerCase() || "").includes(term) ||
      String(claim.claim_id || "").includes(searchTerm)
    );
  });

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.spinnerContainer}>
          <div className={styles.spinner}></div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>{isReadOnly ? "Free Stringing Credits" : "Free Service Management"}</h1>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          Back
        </button>
      </div>

      {successMessage && (
        <div className={styles.successMessage}>{successMessage}</div>
      )}
      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.filters}>
        <input
          type="text"
          placeholder="Search by name, email, phone, racket, or claim ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Claim ID</th>
              <th>Customer Details</th>
              <th>Racket</th>
              <th>Total Services</th>
              <th>Used</th>
              <th>Remaining</th>
              <th>Valid Until</th>
              {!isReadOnly && <th>Actions</th>}
            </tr>
          </thead>

          <tbody>
            {filteredClaims.length === 0 ? (
              <tr>
                <td colSpan={isReadOnly ? 7 : 8} className={styles.noData}>
                  No claims found
                </td>
              </tr>
            ) : (
              filteredClaims.map((claim) => (
                <tr key={claim.claim_id}>
                  <td>{claim.claim_id}</td>

                  <td>
                    <div className={styles.customerInfo}>
                      <div className={styles.customerName}>
                        {claim.customer_name || "N/A"}
                      </div>
                      <div className={styles.customerEmail}>
                        {claim.customer_email || ""}
                      </div>
                      {claim.customer_phone && (
                        <div className={styles.customerPhone}>
                          {claim.customer_phone}
                        </div>
                      )}
                    </div>
                  </td>

                  <td>{claim.racket_name || "N/A"}</td>

                  <td>
                    {editingClaim === claim.claim_id ? (
                      <input
                        type="number"
                        min={0}
                        value={editForm.totalFreeServices}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            totalFreeServices: e.target.value,
                          })
                        }
                        className={styles.editInput}
                      />
                    ) : (
                      claim.total_free_services
                    )}
                  </td>

                  <td>
                    {editingClaim === claim.claim_id && isSuperadmin ? (
                      <input
                        type="number"
                        min={0}
                        value={editForm.usedServices}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            usedServices: e.target.value,
                          })
                        }
                        className={styles.editInput}
                      />
                    ) : (
                      claim.used_services
                    )}
                  </td>

                  <td>
                    <strong>
                      {editingClaim === claim.claim_id
                        ? Math.max(
                            0,
                            parseInt(editForm.totalFreeServices, 10) -
                              parseInt(editForm.usedServices, 10)
                          ) || 0
                        : claim.remaining_services}
                    </strong>
                  </td>

                  <td>
                    {formatDate(claim.valid_until)}
                  </td>

                  {!isReadOnly && (
                  <td>
                    <div className={styles.actions}>
                      {editingClaim === claim.claim_id ? (
                        <>
                          <button
                            onClick={() => handleSaveEdit(claim)}
                            className={`${styles.actionBtn} ${styles.saveBtn}`}
                            title="Save"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className={`${styles.actionBtn} ${styles.cancelBtn}`}
                            title="Cancel"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEditClick(claim)}
                            className={`${styles.actionBtn} ${styles.editBtn}`}
                            title="Edit"
                          >
                            Edit
                          </button>

                          <button
                            onClick={() => handleDeleteClick(claim.claim_id)}
                            className={`${styles.actionBtn} ${styles.deleteBtn}`}
                            title="Delete"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.summary}>
        <p>
          Showing <strong>{filteredClaims.length}</strong> of{" "}
          <strong>{claims.length}</strong> claims
        </p>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3>Confirm Delete</h3>
            </div>

            <div className={styles.modalBody}>
              <p>Are you sure you want to delete this claim?</p>
              <p style={{ marginTop: "8px", fontWeight: "bold" }}>
                This will permanently remove all free services for this racket.
                This action cannot be undone.
              </p>
            </div>

            <div className={styles.modalFooter}>
              <button
                className={`${styles.modalBtn} ${styles.modalBtnCancel}`}
                onClick={handleDeleteCancel}
              >
                Cancel
              </button>
              <button
                className={`${styles.modalBtn} ${styles.modalBtnConfirm}`}
                onClick={handleDeleteConfirm}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FreeServiceManagement;
